import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type SheetSettings, type SheetOverrideColumn } from '../lib/api';

/**
 * Google Sheet sync configuration.
 *
 * A sheet edit only ever *stages* a change here — it lands in the same
 * Pending queue an inline edit in the table does, and nothing reaches a
 * marketplace until a person reviews it there and clicks Sync. This screen
 * exists to configure and test the connection, and to trigger a pull by
 * hand; the automatic pull runs on the same schedule as the intraday sync.
 */
export function SheetSync() {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: ['sheet-settings'], queryFn: api.sheetSettings });
  const { data: channelsData } = useQuery({ queryKey: ['admin-channels'], queryFn: api.adminChannels });

  const [form, setForm] = useState<SheetSettings | null>(null);
  const [serviceAccountJson, setServiceAccountJson] = useState('');
  const [clearKey, setClearKey] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [testResult, setTestResult] = useState<
    { ok: true; headers: string[]; missing: string[] } | { ok: false; error: string } | null
  >(null);
  const [syncResult, setSyncResult] = useState<Awaited<ReturnType<typeof api.runSheetSync>> | null>(null);
  const [skuFilterText, setSkuFilterText] = useState('');
  const requestedSkus = () =>
    skuFilterText
      .split(/[,\n]+/)
      .map((s) => s.trim())
      .filter(Boolean);

  useEffect(() => {
    if (data && form === null) setForm(data.settings);
  }, [data, form]);

  const save = useMutation({
    mutationFn: () => {
      if (!form) throw new Error('not loaded');
      return api.saveSheetSettings({
        enabled: form.enabled,
        spreadsheetId: form.spreadsheetId,
        pricesTab: form.pricesTab,
        overrideColumns: form.overrideColumns,
        liveColumns: form.liveColumns,
        productHeader: form.productHeader,
        siteHeader: form.siteHeader,
        syncStatusHeader: form.syncStatusHeader,
        lastSyncedHeader: form.lastSyncedHeader,
        warnPercent: form.warnPercent,
        maxStagedPerRun: form.maxStagedPerRun,
        // Absent leaves the stored key alone; an empty string clears it —
        // same distinction the SMTP password makes in Notifications.tsx.
        ...(clearKey ? { serviceAccountJson: '' } : serviceAccountJson !== '' ? { serviceAccountJson } : {}),
      });
    },
    onSuccess: (response) => {
      setServiceAccountJson('');
      setClearKey(false);
      setForm(response.settings);
      setResult({ ok: true, message: 'Saved' });
      void queryClient.invalidateQueries({ queryKey: ['sheet-settings'] });
    },
    onError: (err: Error) => setResult({ ok: false, message: err.message }),
  });

  const test = useMutation({
    mutationFn: api.testSheetConnection,
    onSuccess: setTestResult,
    onError: (err: Error) => setTestResult({ ok: false, error: err.message }),
  });

  const runDry = useMutation({
    mutationFn: () => api.runSheetSync(true, requestedSkus()),
    onSuccess: setSyncResult,
  });

  const runReal = useMutation({
    mutationFn: () => api.runSheetSync(false, requestedSkus()),
    onSuccess: (r) => {
      setSyncResult(r);
      void queryClient.invalidateQueries({ queryKey: ['queue'] });
    },
  });

  if (isLoading || !form) return <div className="empty-state">Loading…</div>;
  if (error) return <div className="alert error">{(error as Error).message}</div>;

  const set = <K extends keyof SheetSettings>(key: K, value: SheetSettings[K]) =>
    setForm((current) => (current ? { ...current, [key]: value } : current));

  const channels = channelsData ?? [];
  const marketplaceChannels = channels.filter((c) => c.type === 'amazon' || c.type === 'etsy' || c.type === 'ebay');

  const columnFor = (list: SheetOverrideColumn[], channelId: string) => list.find((c) => c.channelId === channelId)?.header ?? '';
  const setColumnFor = (
    key: 'overrideColumns' | 'liveColumns',
    channelId: string,
    channelName: string,
    header: string,
  ) => {
    const existing = form[key].filter((c) => c.channelId !== channelId);
    set(key, header.trim() === '' ? existing : [...existing, { channelId, channelName, header }]);
  };

  const busy = save.isPending || test.isPending || runDry.isPending || runReal.isPending;

  return (
    <div className="card" style={{ maxWidth: 720 }}>
      <h3 style={{ marginTop: 0 }}>Google Sheet sync</h3>
      <p className="small muted" style={{ marginTop: 0 }}>
        Prices edited in the sheet are staged into the Pending queue, exactly like an inline edit in
        the table — nothing reaches a channel until a person reviews it there and syncs. Prices
        edited in the app are written back to the sheet's Live columns on the same schedule as the
        intraday sync.
      </p>
      <p className="small muted" style={{ marginTop: 0 }}>
        Preview and Pull below, and on the Sheet Sync tab, always work as soon as the connection is
        configured — the checkbox only controls the automatic background pull.
      </p>

      {data?.configError && !save.isPending && <div className="alert warn small">{data.configError}</div>}
      {result && (
        <div className={`alert ${result.ok ? 'ok' : 'error'}`} onClick={() => setResult(null)}>
          {result.message}
        </div>
      )}

      <div className="field">
        <label>
          <input type="checkbox" checked={form.enabled} onChange={(e) => set('enabled', e.target.checked)} />{' '}
          Enable scheduled sync
        </label>
        <span className="small muted">
          Runs an automatic pull-and-write-back at :15 and :45, alongside the intraday sync. Leave
          this off while you're setting things up — Test connection, Preview pull and Pull now below
          are unaffected by it.
        </span>
      </div>

      <div className="field">
        <label htmlFor="spreadsheetId">Spreadsheet id</label>
        <input
          id="spreadsheetId"
          value={form.spreadsheetId}
          placeholder="the long id in the sheet's URL"
          onChange={(e) => set('spreadsheetId', e.target.value)}
        />
      </div>

      <div className="field">
        <label htmlFor="pricesTab">Prices tab name</label>
        <input id="pricesTab" value={form.pricesTab} onChange={(e) => set('pricesTab', e.target.value)} />
      </div>

      <div className="field">
        <label htmlFor="svcJson">Service account key (JSON)</label>
        <textarea
          id="svcJson"
          rows={4}
          value={serviceAccountJson}
          disabled={clearKey}
          placeholder={form.hasServiceAccountKey ? '•••••••• (stored — paste to replace)' : 'Paste the whole downloaded JSON file'}
          onChange={(e) => setServiceAccountJson(e.target.value)}
        />
        <span className="small muted">
          {form.hasServiceAccountKey
            ? `Stored. Share the sheet with ${form.serviceAccountEmail || 'the service account email'} as an Editor.`
            : 'Not set. A service account has no Drive storage of its own — share an existing sheet with it.'}
        </span>
        {form.hasServiceAccountKey && (
          <label className="small">
            <input
              type="checkbox"
              checked={clearKey}
              onChange={(e) => {
                setClearKey(e.target.checked);
                if (e.target.checked) setServiceAccountJson('');
              }}
            />{' '}
            remove the stored key
          </label>
        )}
      </div>

      <h4>Marketplace override columns</h4>
      <p className="small muted" style={{ marginTop: 0 }}>
        Optional. Leave blank to price a channel from the 3rd Party column; name a header here to
        give it its own column instead.
      </p>
      {marketplaceChannels.map((c) => (
        <div className="field-row" key={c.id}>
          <span className="small" style={{ width: 180 }}>
            {c.name}
          </span>
          <input
            style={{ flex: 1 }}
            value={columnFor(form.overrideColumns, c.id)}
            placeholder="e.g. Amazon"
            onChange={(e) => setColumnFor('overrideColumns', c.id, c.name, e.target.value)}
          />
        </div>
      ))}

      <h4>Live price columns (written by the app)</h4>
      {[...channels]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((c) => (
          <div className="field-row" key={c.id}>
            <span className="small" style={{ width: 180 }}>
              {c.name}
            </span>
            <input
              style={{ flex: 1 }}
              value={columnFor(form.liveColumns, c.id)}
              placeholder="e.g. Live — Amazon"
              onChange={(e) => setColumnFor('liveColumns', c.id, c.name, e.target.value)}
            />
          </div>
        ))}

      <div className="field-row" style={{ marginTop: 12 }}>
        <div className="field" style={{ width: 160 }}>
          <label htmlFor="warnPercent">Large-change warning</label>
          <input
            id="warnPercent"
            type="number"
            value={form.warnPercent}
            onChange={(e) => set('warnPercent', Number(e.target.value))}
          />
          <span className="small muted">Percent</span>
        </div>
        <div className="field" style={{ width: 160 }}>
          <label htmlFor="maxStaged">Max staged per run</label>
          <input
            id="maxStaged"
            type="number"
            value={form.maxStagedPerRun}
            onChange={(e) => set('maxStagedPerRun', Number(e.target.value))}
          />
          <span className="small muted">Refuses the run above this</span>
        </div>
      </div>

      <div className="field" style={{ marginTop: 12 }}>
        <label htmlFor="skuFilter">Only these SKUs (optional)</label>
        <input
          id="skuFilter"
          value={skuFilterText}
          placeholder="e.g. NWC-Twin, UF-MB — leave blank to sync every row"
          onChange={(e) => setSkuFilterText(e.target.value)}
        />
        <span className="small muted">
          Comma or newline separated. Restricts Preview/Pull to just these rows — every other row is left
          completely untouched, so this is the safe way to try a pull on a couple of SKUs first.
        </span>
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button className="primary" disabled={busy} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
        <button disabled={busy} onClick={() => test.mutate()}>
          {test.isPending ? 'Testing…' : 'Test connection'}
        </button>
        <button disabled={busy} onClick={() => runDry.mutate()}>
          {runDry.isPending ? 'Planning…' : 'Preview pull (no changes)'}
        </button>
        <button disabled={busy} onClick={() => runReal.mutate()}>
          {runReal.isPending ? 'Syncing…' : 'Pull now'}
        </button>
      </div>

      {testResult && (
        <div className={`alert small ${testResult.ok ? 'ok' : 'error'}`} style={{ marginTop: 12 }}>
          {testResult.ok ? (
            <>
              Found headers: {testResult.headers.join(', ')}
              {testResult.missing.length > 0 && (
                <div>Missing required: {testResult.missing.join(', ')}</div>
              )}
            </>
          ) : (
            testResult.error
          )}
        </div>
      )}

      {syncResult && (
        <div className={`alert small ${syncResult.ok ? 'ok' : 'error'}`} style={{ marginTop: 12 }}>
          {syncResult.dryRun && <strong>Preview only — nothing was staged. </strong>}
          {syncResult.skuAllowlistMisses.length > 0 && (
            <div>Not found in the sheet: {syncResult.skuAllowlistMisses.join(', ')}</div>
          )}
          {syncResult.error ?? (
            <>
              {syncResult.rowsRead} rows read, {syncResult.staged} staged, {syncResult.unchanged} unchanged,{' '}
              {syncResult.unmatched} unmatched, {syncResult.rejected} rejected, {syncResult.collisions}{' '}
              collision{syncResult.collisions === 1 ? '' : 's'}.
            </>
          )}
          {syncResult.notes.length > 0 && (
            <ul style={{ marginBottom: 0, maxHeight: 200, overflowY: 'auto' }}>
              {syncResult.notes.slice(0, 50).map((n, i) => (
                <li key={i} className="small">
                  row {n.rowNumber} ({n.skuDisplay}): {n.message}
                </li>
              ))}
              {syncResult.notes.length > 50 && <li className="small muted">…and {syncResult.notes.length - 50} more</li>}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
